import { Share2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { useClipboard } from "@/hooks/use-clipboard"
import { cn } from "@/lib/utils"

/**
 * OKS-21 — Native share button.
 *
 * Primary action on **mobile** (full-width sticky CTA per UX Spec
 * §2.1). On desktop, slots second after Copy.
 *
 * Behavior:
 *   1. If `navigator.share` is available (mobile + modern desktop),
 *      invokes the Web Share API with brand-prefilled text.
 *   2. If unsupported OR user cancels, **silently falls back to copy**
 *      (UX Spec §4 micro-interactions — desktop with no Web Share
 *      → fall back silently to Copy). On copy success, fires a
 *      `share_unsupported` toast so the user knows the link is on
 *      their clipboard.
 *
 * `AbortError` is treated as user-cancel and is NOT shown as an
 * error — per Web Share API spec the user dismissed the sheet
 * intentionally.
 */

interface InviteShareButtonProps {
  url: string
  brandName: string
  /** Tailwind className override (e.g. for full-width sticky on mobile). */
  className?: string
  /** Variant override — defaults to "outline" on desktop. Mobile usage
   *  passes "default" since Share is the mobile primary. */
  variant?: "default" | "outline" | "secondary"
  /** Override testid. Defaults to `invites-panel-share-button` per UX Spec §6. */
  testId?: string
}

export function InviteShareButton({
  url,
  brandName,
  className,
  variant = "outline",
  testId = "invites-panel-share-button",
}: InviteShareButtonProps) {
  const { t } = useTranslation()
  const [, copy] = useClipboard()

  const handleShare = async () => {
    const text = t("invites.share_message", { brand_name: brandName, url })

    // Capability check — `navigator.share` is undefined in SSR + older
    // desktops. typeof check is the canonical guard per the Web
    // Share API "Feature detection" recipe.
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function"
    ) {
      try {
        await navigator.share({ title: brandName, text, url })
        return
      } catch (err) {
        // User-cancel via the share sheet. Per spec, AbortError is
        // dismissed-by-user; not an error condition. Any other error
        // (e.g. transient OS-level rejection) falls through to the
        // copy-fallback path.
        if (err instanceof Error && err.name === "AbortError") {
          return
        }
      }
    }

    // Fallback: copy URL silently + tell the user via toast.
    const ok = await copy(url)
    if (ok) {
      toast(t("invites.panel.toast.share_unsupported"), {
        id: "toast-invite-copied",
      })
    } else {
      toast.error(t("invites.panel.toast.copy_failed"))
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      onClick={() => {
        void handleShare()
      }}
      data-testid={testId}
      aria-label={t("invites.panel.actions.share")}
      className={cn(className)}
    >
      <Share2 className="size-4" aria-hidden="true" />
      {t("invites.panel.actions.share")}
    </Button>
  )
}
